import { type Dispatch, type SetStateAction, useMemo } from "react";
import { useSetTodoStepDone } from "./useSetTodoStepDone";
import { TodoSpineRow } from "./TodoSpineRow";
import { todoSpineRunEdges } from "./todoSpineRun";
import {
  autoExpandedGroupIds,
  isGroupExpanded,
  pinGroupsOpen,
} from "./todoExpansion";
import { TodoResumeCard } from "./TodoResumeCard";
import {
  firstUnfinishedStep,
  todoPhaseLabels,
  type TodoPhase,
  type TodoRail,
  type TodoStep,
} from "@lib/todoRail/todoRailModel";
import { refreshTodoFocusRequest } from "./todoFocus";

const phaseOrder: TodoPhase[] = ["watch", "score", "allocate"];

export function TodoDrawerBody({
  rail,
  isLoading,
  roundId,
  onNavigate,
  expanded,
  setExpanded,
}: {
  // Passed in rather than computed here: the drawer header renders the same
  // rail, and building it twice would mean two useMemos over the aggregate.
  rail: TodoRail | null;
  isLoading: boolean;
  roundId: string;
  onNavigate: (href: string) => void;
  expanded: Record<string, boolean>;
  setExpanded: Dispatch<SetStateAction<Record<string, boolean>>>;
}) {
  const setStepDone = useSetTodoStepDone(roundId);

  // This tab is the PLAN, so it always leads with the next unfinished step —
  // never the most recent place. "Where was I" is the History tab's question,
  // and answering both here is what made one card contradict itself.
  const targetStep = useMemo(
    () => firstUnfinishedStep(rail?.steps ?? []),
    [rail],
  );

  // Groups holding the current step open themselves. Kept SEPARATE from the
  // `expanded` map rather than seeding it, so this stays derived: when the
  // current step moves to another group, the new group opens and the old one
  // falls shut on its own. Seeding state once would leave a trail of groups the
  // funder never opened. See todoExpansion for the two sources and which wins.
  const autoExpanded = useMemo(
    () =>
      autoExpandedGroupIds(
        rail?.steps ?? [],
        targetStep?.id,
        (targetStep?.children.length ?? 0) > 0,
      ),
    [rail, targetStep],
  );

  if (rail == null) {
    // A read failed, so there is no rail to show — and saying "loading" here
    // would be a spinner with nothing behind it. The queries retry and refetch
    // on focus, so this state clears itself.
    return (
      <div className="px-6 text-body-sm text-text-muted">
        {isLoading ? "Loading your round…" : "Couldn’t load your round."}
      </div>
    );
  }

  const openStep = (step: TodoStep) => {
    if (step.href == null) {
      return;
    }

    // Nothing is recorded from this side: History records where you went, and
    // the destination's own recorder fires on arrival. A second pointer
    // maintained here is what let the Resume card disagree with the list.
    onNavigate(refreshTodoFocusRequest(step.href));
  };

  const renderStep = (
    step: TodoStep,
    depth: number,
    isFirst: boolean,
    isLast: boolean,
  ) => {
    return (
      <TodoSpineRow
        key={step.id}
        step={step}
        depth={depth}
        isCurrent={targetStep?.id === step.id}
        isFirst={isFirst}
        isLast={isLast}
        // An explicit toggle always wins, so the funder can close the group
        // the current step opened.
        isExpanded={isGroupExpanded(expanded, autoExpanded, step.id)}
        // Negate the DISPLAYED state, not the raw map. An auto-expanded group
        // has no entry, so negating `?? false` wrote `true` over a row already
        // showing expanded — the first click on the chevron did nothing and the
        // funder had to click twice to collapse the group the rail opened.
        onToggleExpand={() =>
          setExpanded((prev) => ({
            ...prev,
            [step.id]: !isGroupExpanded(prev, autoExpanded, step.id),
          }))
        }
        onOpen={() => openStep(step)}
        onSetDone={
          step.manual != null
            ? (done) => {
                // Acting on a row must never move the row — see pinGroupsOpen.
                setExpanded((prev) => pinGroupsOpen(prev, rail.steps, step.id));
                setStepDone(step.manual!, done);
              }
            : undefined
        }
      >
        {renderList(step.children, depth + 1)}
      </TodoSpineRow>
    );
  };

  /**
   * One run of sibling rows — a phase's steps, or the rows inside an expanded
   * group.
   *
   * The spine is trimmed to the FIRST and LAST row of the run that actually
   * draws it, which is why this is computed per list rather than per row. It
   * used to be "first/last in the phase, top level only", so every child list
   * drew a full-height line: a group's line ran from above its first node to
   * below its last one, dangling at both ends, and a run of one drew a stub
   * attached to nothing.
   */
  const renderList = (steps: TodoStep[], depth: number) => {
    const { first, last } = todoSpineRunEdges(steps);

    return steps.map((step) =>
      renderStep(step, depth, step === first, step === last),
    );
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      {targetStep != null && (
        <TodoResumeCard
          title={targetStep.title}
          detail={targetStep.progress.detail}
          step={targetStep}
          disabled={targetStep.href == null}
          eyebrow="Next on the list"
          action={targetStep.kind === "video" ? "Play" : "Open"}
          onOpen={() => openStep(targetStep)}
        />
      )}

      {/* `scrollbar-gutter: stable` reserves the scrollbar's strip whether or
          not it is showing. The drawer is a fixed width, so a scrollbar
          appearing takes its space out of the CONTENT — and every title in the
          list rewraps. Expanding a group is exactly what makes it appear, so
          the row you were reading jumped sideways each time you opened one. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-6 pb-6 [scrollbar-gutter:stable]">
        {phaseOrder.map((phase) => {
          const steps = rail.steps.filter((step) => step.phase === phase);

          if (steps.length === 0) {
            return null;
          }

          return (
            <section key={phase}>
              <h3 className="flex items-center gap-2 pt-4 pb-1.5 pl-10.5 text-label-sm tracking-wider text-text-muted uppercase">
                {todoPhaseLabels[phase]}
                <span className="h-px flex-1 bg-border-subtle" />
              </h3>
              {renderList(steps, 0)}
            </section>
          );
        })}
      </div>
    </div>
  );
}
